import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2,
  RefreshCcw,
  ShieldCheck,
  ShieldOff,
  Mail,
  X,
  Crown,
  Plus,
  Clock,
} from "lucide-react";

import { adminAuthHeader } from "../../lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => adminAuthHeader();

function formatDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

/**
 * TeamTab , the owner-only "Manage admins" screen.
 *
 * Lists every admin (active + revoked) plus pending invites, with
 * actions to revoke, reactivate, cancel a pending invite, or trigger
 * the existing InviteAdminModal.
 *
 * The Team tab is only rendered for owners on the parent
 * AdminDashboardPage. We still no-op the destructive actions when the
 * server says 403 , defence in depth.
 */
export default function TeamTab({ currentEmail, onInvite }) {
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [pending, setPending] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const fetchTeam = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/auth/users`, {
        headers: getAuthHeaders(),
      });
      setAdmins(data.admins || []);
      setPending(data.pending_invites || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't load the team.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  const revoke = async (admin) => {
    if (
      !window.confirm(
        `Revoke access for ${admin.email}? They'll be signed out and lose access to the admin dashboard. You can reactivate them at any time.`,
      )
    )
      return;
    setBusyId(admin.id);
    try {
      await axios.post(
        `${API}/admin/auth/users/${admin.id}/revoke`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success(`${admin.email} has been revoked.`);
      await fetchTeam();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't revoke that admin.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const reactivate = async (admin) => {
    setBusyId(admin.id);
    try {
      await axios.post(
        `${API}/admin/auth/users/${admin.id}/reactivate`,
        {},
        { headers: getAuthHeaders() },
      );
      toast.success(`${admin.email} has been reactivated.`);
      await fetchTeam();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't reactivate that admin.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const cancelInvite = async (invite) => {
    if (
      !window.confirm(
        `Cancel the pending invite for ${invite.email}? The accept-invite link will stop working immediately.`,
      )
    )
      return;
    setBusyId(invite.id);
    try {
      await axios.delete(`${API}/admin/auth/invites/${invite.id}`, {
        headers: getAuthHeaders(),
      });
      toast.success(`Invite for ${invite.email} cancelled.`);
      await fetchTeam();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Couldn't cancel that invite.",
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-team-tab">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-['Outfit'] font-bold tracking-tight text-[var(--dark-bg)]">
            Team
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Manage who can access the MediTalk admin dashboard. Only the owner
            can revoke access or invite new admins.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={onInvite}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[var(--lime)] text-[var(--dark-bg)] text-sm font-bold hover:bg-[var(--lime-dark)] transition-colors shadow-sm"
            data-testid="team-invite-btn"
          >
            <Plus size={16} strokeWidth={2.5} />
            Invite admin
          </button>
          <button
            onClick={fetchTeam}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[var(--light-border)] bg-white text-[var(--charcoal)] text-sm font-semibold hover:bg-[var(--bone-soft)]"
            data-testid="team-refresh-btn"
          >
            <RefreshCcw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[var(--text-muted)] text-sm">
          <Loader2 size={16} className="animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="space-y-8">
          {/* ----- Active + revoked admins ------------------------------ */}
          <section>
            <h2 className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-3">
              Admin accounts
            </h2>
            <div className="rounded-2xl border border-[var(--light-border)] bg-white overflow-hidden">
              {admins.length === 0 ? (
                <p className="px-5 py-6 text-sm text-[var(--text-muted)]">
                  No admin accounts found.
                </p>
              ) : (
                <ul className="divide-y divide-[var(--light-border)]">
                  {admins.map((a) => {
                    const isYou = a.email === currentEmail;
                    const isOwnerRow = a.role === "owner";
                    return (
                      <li
                        key={a.id}
                        className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-4 ${
                          a.active ? "" : "bg-rose-50/40"
                        }`}
                        data-testid={`admin-row-${a.id}`}
                      >
                        <div className="flex items-start gap-3 min-w-0">
                          <span
                            className={`w-9 h-9 flex-shrink-0 rounded-full flex items-center justify-center ${
                              isOwnerRow
                                ? "bg-[var(--lime)]/20 text-[var(--charcoal)]"
                                : a.active
                                  ? "bg-[var(--bone-soft)] text-[var(--charcoal)]"
                                  : "bg-rose-100 text-rose-600"
                            }`}
                          >
                            {isOwnerRow ? <Crown size={16} /> : <ShieldCheck size={16} />}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-[var(--charcoal)] truncate">
                              {a.email}
                              {isYou && (
                                <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-[var(--charcoal)]/70">
                                  You
                                </span>
                              )}
                            </p>
                            <p className="text-xs text-[var(--text-muted)] mt-0.5">
                              {isOwnerRow ? "Owner" : "Admin"} ·{" "}
                              {a.active ? "Active" : "Revoked"}
                              {a.last_login_at
                                ? ` · last login ${formatDate(a.last_login_at)}`
                                : " · never signed in"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {isYou ? (
                            <span className="text-xs text-[var(--text-muted)] italic px-3">
                              That's you
                            </span>
                          ) : a.active ? (
                            <button
                              type="button"
                              onClick={() => revoke(a)}
                              disabled={busyId === a.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-rose-300 text-rose-700 text-xs font-semibold hover:bg-rose-50 disabled:opacity-60"
                              data-testid={`revoke-${a.id}`}
                            >
                              {busyId === a.id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <ShieldOff size={12} />
                              )}
                              Revoke
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => reactivate(a)}
                              disabled={busyId === a.id}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--charcoal)] text-white text-xs font-semibold hover:bg-[var(--dark-bg)] disabled:opacity-60"
                              data-testid={`reactivate-${a.id}`}
                            >
                              {busyId === a.id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <ShieldCheck size={12} />
                              )}
                              Reactivate
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </section>

          {/* ----- Pending invites ------------------------------------- */}
          <section>
            <h2 className="text-xs uppercase tracking-wider font-semibold text-[var(--text-muted)] mb-3">
              Pending invites
            </h2>
            <div className="rounded-2xl border border-[var(--light-border)] bg-white overflow-hidden">
              {pending.length === 0 ? (
                <p className="px-5 py-6 text-sm text-[var(--text-muted)]">
                  No pending invites. Click <strong>Invite admin</strong> above
                  to add someone.
                </p>
              ) : (
                <ul className="divide-y divide-[var(--light-border)]">
                  {pending.map((inv) => (
                    <li
                      key={inv.id}
                      className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-5 py-4"
                      data-testid={`invite-row-${inv.id}`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <span className="w-9 h-9 flex-shrink-0 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
                          <Mail size={16} />
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-[var(--charcoal)] truncate">
                            {inv.email}
                          </p>
                          <p className="text-xs text-[var(--text-muted)] mt-0.5 flex items-center gap-1.5">
                            <Clock size={11} />
                            Invited {formatDate(inv.created_at)} · expires{" "}
                            {formatDate(inv.expires_at)} · invited by{" "}
                            {inv.invited_by_email}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => cancelInvite(inv)}
                          disabled={busyId === inv.id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-[var(--light-border)] text-[var(--charcoal)] text-xs font-semibold hover:bg-[var(--bone-soft)] disabled:opacity-60"
                          data-testid={`cancel-invite-${inv.id}`}
                        >
                          {busyId === inv.id ? (
                            <Loader2 size={12} className="animate-spin" />
                          ) : (
                            <X size={12} />
                          )}
                          Cancel invite
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
